import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Users, User, Sparkles, Video, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function minToHM(m) {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

const FragmentRow = ({ children }) => <>{children}</>;

const SERVICE_DEFAULT_DURATION = {
  rieducazione_motoria: 60,
  small_group: 60,
  valutazione_iniziale: 60,
  coaching_online: 45,
  personal_training: 60,
};

function timeToMinutes(t) {
  const [h, m] = (t || "").split(":");
  return (parseInt(h) || 0) * 60 + (parseInt(m) || 0);
}

// Cells occupied by a slot: e.g. 60 min → 2 cells, 90 min → 3, 45 min → 2 (rounded up).
function slotCellSpan(slot) {
  const dur = slot?.duration_minutes || SERVICE_DEFAULT_DURATION[slot?.service_type] || 60;
  return Math.max(1, Math.ceil(dur / 30));
}

const DEFAULT_SERVICES = [
  { key: "rieducazione_motoria", label: "Rieducazione motoria", capacity: 1, color: "#A855F7", icon: <User size={14} /> },
  { key: "small_group", label: "Small group", capacity: 4, color: "#EC4899", icon: <Users size={14} /> },
  { key: "valutazione_iniziale", label: "Valutazione iniziale", capacity: 1, color: "#F97316", icon: <Sparkles size={14} /> },
  { key: "coaching_online", label: "Coaching online", capacity: 1, color: "#06B6D4", icon: <Video size={14} /> },
  { key: "personal_training", label: "Personal training", capacity: 1, color: "#10B981", icon: <User size={14} /> },
];

function iconForService(key) {
  if (key === "small_group") return <Users size={14} />;
  if (key === "valutazione_iniziale") return <Sparkles size={14} />;
  if (key === "coaching_online") return <Video size={14} />;
  return <User size={14} />;
}

const HOURS = (() => {
  const out = [];
  for (let h = 7; h < 21; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
})();

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

export default function WeeklyCalendar({ role, onChanged, clients = [], initialDate, onWeekChange }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(initialDate ? new Date(initialDate) : new Date()));
  const [slots, setSlots] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cellDialog, setCellDialog] = useState(null);
  const [slotDialog, setSlotDialog] = useState(null);
  const [repeatCount, setRepeatCount] = useState(0);
  const [customDuration, setCustomDuration] = useState(60);
  const [customCapacity, setCustomCapacity] = useState("");
  const [bookForClientId, setBookForClientId] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [rescheduleDialog, setRescheduleDialog] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ new_date: "", new_time: "" });
  // Live services list from backend — label & color reflect Settings updates in real time.
  const [SERVICES, setServices] = useState(DEFAULT_SERVICES);
  const reloadServices = () => api.get("/services").then(r => {
    const withIcons = r.data.map(s => ({ ...s, icon: iconForService(s.key) }));
    setServices(withIcons);
    // apply colors to CSS custom properties so `.svc-<key>` picks them up
    withIcons.forEach(s => document.documentElement.style.setProperty(`--svc-${s.key}`, s.color || "#6E667A"));
  }).catch(() => {});

  useEffect(() => { reloadServices(); }, []);

  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);
  const [trainers, setTrainers] = useState([]);
  useEffect(() => { api.get("/trainers").then(r => setTrainers(r.data)).catch(() => {}); }, []);
  // StrictMode-safe: only report weekStart to parent when the user actually
  // paged the calendar (cal-prev/cal-next/setWeekStart), not on initial mount.
  // Compare the current Monday against the Monday derived from `initialDate` prop.
  const initialMonday = useMemo(
    () => fmtDate(startOfWeek(initialDate ? new Date(initialDate) : new Date())),
    [initialDate]
  );
  useEffect(() => {
    const currentMonday = fmtDate(weekStart);
    if (currentMonday === initialMonday) return; // still showing the week we mounted on
    onWeekChange && onWeekChange(currentMonday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, initialMonday]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const load = async () => {
    const from = fmtDate(days[0]);
    const to = fmtDate(days[6]);
    try {
      const { data } = await api.get(`/slots?from_date=${from}&to_date=${to}`);
      setSlots(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const findSlot = (date, time) => slots.find((s) => s.date === date && s.time === time);
  const findSlots = (date, time) => slots.filter((s) => s.date === date && s.time === time);

  // Parallel lanes: one column per trainer that has slots on that day.
  const laneInfo = (slot) => {
    const order = trainers.map(t => t.id);
    const ids = [...new Set(slots.filter(s => s.date === slot.date).map(s => s.trainer_id))]
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return { idx: Math.max(0, ids.indexOf(slot.trainer_id)), count: Math.max(1, ids.length) };
  };

  // Slot that starts earlier the same day and extends over `time`.
  const findCoveringSlot = (date, time) => {
    const tMin = timeToMinutes(time);
    return slots.find((s) => {
      if (s.date !== date) return false;
      const sStart = timeToMinutes(s.time);
      if (sStart >= tMin) return false;
      const sEnd = sStart + (s.duration_minutes || SERVICE_DEFAULT_DURATION[s.service_type] || 60);
      return sEnd > tMin;
    });
  };

  const isPast = (date, time) => {
    const [hh, mm] = time.split(":");
    const d = new Date(date + "T" + hh + ":" + mm);
    return d < new Date();
  };

  const hoursStatus = (cfg, time) => {
    if (!cfg || cfg.closed) return "outside";
    if (cfg.open && time < cfg.open) return "outside";
    if (cfg.close && time >= cfg.close) return "outside";
    if (cfg.lunch_from && cfg.lunch_to && time >= cfg.lunch_from && time < cfg.lunch_to) return "lunch";
    return "ok";
  };

  const cellStatus = (date, time) => {
    // returns "outside" | "lunch" | "ok" — ok if at least one trainer is working
    const d = new Date(date);
    const weekday = (d.getDay() + 6) % 7;
    const all = trainers.length ? trainers.map(t => t.working_hours) : (settings ? [settings.working_hours] : []);
    if (!all.length) return "ok";
    const st = all.map(h => hoursStatus((h || [])[weekday], time));
    if (st.includes("ok")) return "ok";
    return st.includes("lunch") ? "lunch" : "outside";
  };

  const handleCellClick = (date, time) => {
    if (isPast(date, time)) return;
    const st = cellStatus(date, time);
    // Admin: empty area of a cell always opens creation (parallel slots for other trainers allowed).
    if (role === "admin") {
      if (st !== "ok") { toast.error(st === "lunch" ? "Pausa pranzo, slot non prenotabile" : "Fuori orario di lavoro"); return; }
      setCellDialog({ date, time });
      return;
    }
    // Ignore clicks on cells covered by a longer slot starting earlier.
    const covering = findCoveringSlot(date, time);
    const existingSlot = findSlot(date, time);
    if (covering && !existingSlot) { setSlotDialog(covering); return; }
    if (st !== "ok" && !existingSlot) {
      toast.error(st === "lunch" ? "Pausa pranzo, slot non prenotabile" : "Fuori orario di lavoro");
      return;
    }
    if (existingSlot) { setSlotDialog(existingSlot); return; }
    // Empty slot: admin opens creation, client opens self-service booking picker
    if (role === "admin") setCellDialog({ date, time });
    else if (role === "client") setCellDialog({ date, time, clientPick: true });
  };

  const clientBookFree = async (service_type) => {
    try {
      const { data: me } = await api.get("/auth/me");
      const remaining = (me.lessons_total || 0) - (me.lessons_used || 0);
      if (remaining <= 0) { toast.error("Abbonamento esaurito"); return; }
      if (remaining < 3 && !window.confirm(`Ti rimangono ${remaining} lezion${remaining === 1 ? "e" : "i"}. Ricordati di rinnovare!\n\nProcedere con la prenotazione?`)) return;
      await api.post("/bookings", { date: cellDialog.date, time: cellDialog.time, service_type });
      toast.success("Prenotato!");
      setCellDialog(null); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createSlot = async (service_type) => {
    try {
      const body = {
        date: cellDialog.date, time: cellDialog.time, service_type,
        repeat_occurrences: repeatCount,
        client_id: bookForClientId || null,
        duration_minutes: customDuration || null,
        capacity_override: customCapacity ? parseInt(customCapacity) : null,
      };
      const { data } = await api.post("/slots", body);
      const bits = [];
      if ((data.created_ids || []).length > 1) bits.push(`${data.created_ids.length} slot creati`);
      else bits.push("Slot creato");
      if (data.booked > 0) bits.push(`${data.booked} prenotazion${data.booked>1?"i":"e"}`);
      toast.success(bits.join(" · "));
      setCellDialog(null); setRepeatCount(0); setBookForClientId("");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDeleteDialog = () => {
    if (!slotDialog) return;
    setDeleteDialog(slotDialog);
    setSlotDialog(null);
  };

  const performDelete = async (scope, client_id = null) => {
    try {
      const params = new URLSearchParams({ scope });
      if (client_id) params.set("client_id", client_id);
      const r = await api.delete(`/slots/${deleteDialog.id}?${params.toString()}`);
      const msg = scope === "single" ? "Slot eliminato"
        : scope === "series_client" ? `${r.data.cancelled_bookings || 0} prenotazioni annullate`
        : `${r.data.deleted_slots || 0} slot eliminati`;
      toast.success(msg);
      setDeleteDialog(null); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSlot = async (id) => {
    // legacy, now goes through openDeleteDialog
    openDeleteDialog();
  };

  const bookForClient = async (slot_id, client_id) => {
    try {
      await api.post("/bookings", { slot_id, client_id });
      toast.success("Prenotazione creata");
      await load(); onChanged?.();
      const updated = (await api.get(`/slots?from_date=${fmtDate(days[0])}&to_date=${fmtDate(days[6])}`)).data.find(s => s.id === slot_id);
      setSlotDialog(updated);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clientBook = async (slot_id) => {
    // low-balance mandatory acknowledgment (<3 residues)
    try {
      const { data: me } = await api.get("/auth/me");
      const remaining = (me.lessons_total || 0) - (me.lessons_used || 0);
      if (remaining <= 0) {
        alert("Abbonamento esaurito. Contatta Sara per rinnovare prima di prenotare.");
        return;
      }
      if (remaining < 3) {
        const ok = window.confirm(`Ti rimangono ${remaining} lezion${remaining === 1 ? "e" : "i"}. Ricordati di rinnovare presto!\n\nProcedere con la prenotazione?`);
        if (!ok) return;
      }
    } catch {}
    try {
      await api.post("/bookings", { slot_id });
      toast.success("Prenotato!");
      await load(); onChanged?.();
      setSlotDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelBooking = async (booking_id) => {
    try {
      await api.delete(`/bookings/${booking_id}`);
      toast.success("Prenotazione annullata");
      await load(); onChanged?.();
      setSlotDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReschedule = (booking) => {
    setRescheduleForm({ new_date: booking.date, new_time: booking.time });
    setRescheduleDialog(booking);
  };

  const doReschedule = async () => {
    if (!rescheduleForm.new_date || !rescheduleForm.new_time) {
      return toast.error("Data e ora richieste");
    }
    try {
      await api.patch(`/bookings/${rescheduleDialog.id}/reschedule`, rescheduleForm);
      toast.success("Prenotazione spostata");
      setRescheduleDialog(null); setSlotDialog(null);
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const replicate = async () => {
    // deprecated (kept for compatibility)
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
            className="w-9 h-9 border border-white/10 hover:border-white/40 flex items-center justify-center"><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="px-3 h-9 border border-white/10 hover:border-white/40 font-heading text-xs uppercase tracking-widest">Oggi</button>
          <button data-testid="cal-next" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
            className="w-9 h-9 border border-white/10 hover:border-white/40 flex items-center justify-center"><ChevronRight size={16} /></button>
          <div className="ml-3 font-heading uppercase tracking-tight text-lg">
            {days[0].getDate()} {MONTHS[days[0].getMonth()]} – {days[6].getDate()} {MONTHS[days[6].getMonth()]} {days[6].getFullYear()}
          </div>
        </div>
        {role === "admin" && (
          <div className="label-caps text-white/40 text-xs">
            Clicca uno slot per aggiungere/modificare
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {SERVICES.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs">
            <span className={`inline-block w-3 h-3 svc-${s.key}`} />
            <span className="text-white/60 uppercase tracking-wider">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="card-surface overflow-x-auto">
        <div className="min-w-[900px] grid" style={{ gridTemplateColumns: "72px repeat(7, 1fr)" }}>
          <div className="p-3 label-caps border-b border-white/10">Ora</div>
          {days.map((d, i) => (
            <div key={i} className="p-3 border-b border-l border-white/10">
              <div className="label-caps">{WEEKDAYS[i]}</div>
              <div className="font-heading font-bold text-lg">{d.getDate()}</div>
            </div>
          ))}
          {HOURS.map((h) => (
            <FragmentRow key={h}>
              <div className="p-3 label-caps text-white/40 border-b border-white/5 font-heading">{h}</div>
              {days.map((d, i) => {
                const date = fmtDate(d);
                const cellSlots = findSlots(date, h);
                const s = cellSlots[0];
                const covering = !s ? findCoveringSlot(date, h) : null;
                const past = isPast(date, h);
                const status = cellStatus(date, h);
                const extraClass = status === "lunch" ? "bg-white/[0.03]" : status === "outside" ? "opacity-30" : "";
                const span = s ? Math.max(...cellSlots.map(slotCellSpan)) : 1;
                return (
                  <div
                    key={h + "-" + i}
                    data-testid={`cell-${date}-${h}`}
                    onClick={() => handleCellClick(date, h)}
                    className={`cal-cell border-l border-white/5 ${past ? "past" : ""} ${extraClass} ${covering ? "cursor-pointer" : ""}`}
                    style={s && span > 1 ? { position: "relative", zIndex: 5 } : undefined}
                  >
                    {status === "lunch" && !s && !covering && (
                      <div className="absolute inset-1 flex items-center justify-center text-[9px] uppercase tracking-widest text-black/50 border border-dashed border-black/20 bg-[#E5E7EB]">
                        Pausa
                      </div>
                    )}
                    {cellSlots.map((s) => {
                      const sSpan = slotCellSpan(s);
                      const { idx, count } = laneInfo(s);
                      return (
                      <div
                        key={s.id}
                        onClick={(e) => { e.stopPropagation(); if (!isPast(date, h) || role === "admin") setSlotDialog(s); }}
                        className={`absolute p-2 flex flex-col justify-between svc-${s.service_type} overflow-hidden cursor-pointer`}
                        style={{
                          top: 4,
                          left: `calc(${(idx * 100) / count}% + 4px)`,
                          width: `calc(${100 / count}% - 8px)`,
                          // extend visually across `span` rows (each row ~ same cell height).
                          height: `calc(${sSpan * 100}% - 8px + ${(sSpan - 1)} * 1px)`,
                          zIndex: 5,
                        }}
                        data-testid={`slot-block-${date}-${h}${idx > 0 ? `-${idx}` : ""}`}
                      >
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider truncate">
                            {SERVICES.find(x => x.key === s.service_type)?.label}
                          </div>
                          {s.trainer_name && trainers.length > 1 && (
                            <div className="text-[9px] uppercase tracking-wider opacity-90 truncate" data-testid={`slot-trainer-${s.id}`}>
                              {s.trainer_name}
                            </div>
                          )}
                          <div className="text-[9px] opacity-70 mt-0.5">
                            {s.time}–{minToHM(timeToMinutes(s.time) + (s.duration_minutes || SERVICE_DEFAULT_DURATION[s.service_type] || 60))}
                          </div>
                        </div>
                        {role === "admin" && s.booked_names?.length > 0 && (
                          <div className="text-[10px] leading-tight opacity-90 line-clamp-2">
                            {s.booked_names.join(", ")}
                          </div>
                        )}
                        <div className="text-[10px] opacity-80">
                          {s.booked_count}/{s.capacity}{s.i_booked && role === "client" ? " · Tu" : ""}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </FragmentRow>
          ))}
        </div>
      </div>

      {/* Create slot dialog */}
      <Dialog open={!!cellDialog} onOpenChange={(o) => { if (!o) { setCellDialog(null); setRepeatCount(0); setBookForClientId(""); } }}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none w-[95vw] max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {cellDialog?.clientPick ? "Prenota" : "Nuovo slot"} · {cellDialog?.date} · {cellDialog?.time}
            </DialogTitle>
          </DialogHeader>

          {cellDialog?.clientPick ? (
            <>
              <div className="text-xs text-white/60 mb-3">
                Seleziona il servizio per prenotare questa fascia oraria. Il Small group va prenotato solo sugli slot già creati dalla trainer.
              </div>
              <div className="grid gap-2">
                {SERVICES.filter(s => (s.capacity || 1) <= 1).map((s) => (
                  <button
                    key={s.key}
                    data-testid={`client-book-${s.key}`}
                    onClick={() => clientBookFree(s.key)}
                    className="p-4 border border-white/10 hover:border-[#A855F7] flex items-center justify-between text-left transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center svc-${s.key}`}>{s.icon}</span>
                      <div>
                        <div className="font-heading font-bold uppercase tracking-wider text-sm">{s.label}</div>
                        <div className="text-xs text-white/50">1 lezione verrà scalata dal pacchetto</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
          <div className="mb-1">
            <span className="label-caps block mb-2">Ripeti settimanalmente</span>
            <select
              data-testid="repeat-count-select"
              value={repeatCount}
              onChange={(e) => setRepeatCount(parseInt(e.target.value))}
              className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none"
            >
              <option value={0} className="bg-[#120B19]">Solo questo slot</option>
              {[1,2,3,4,5,6,7,8,10,12,16,20,24,52].map(n => {
                const label = `+ ${n} ${n > 1 ? "settimane" : "settimana"} (${n+1} totali)`;
                return <option key={n} value={n} className="bg-[#120B19]">{label}</option>;
              })}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <div>
              <span className="label-caps block mb-2">Durata (min)</span>
              <select data-testid="slot-duration" value={customDuration} onChange={e => setCustomDuration(parseInt(e.target.value))}
                className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none">
                {[30, 45, 60, 75, 90, 120].map(m => <option key={m} value={m} className="bg-[#120B19]">{m} min</option>)}
              </select>
            </div>
            <div>
              <span className="label-caps block mb-2">Capienza override</span>
              <input data-testid="slot-capacity" type="number" min="1" value={customCapacity} onChange={e => setCustomCapacity(e.target.value)} placeholder="Default"
                className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
            </div>
          </div>

          <div className="mt-3">
            <span className="label-caps block mb-2">Prenota per cliente (opzionale)</span>
            <select
              data-testid="book-client-select"
              value={bookForClientId}
              onChange={(e) => setBookForClientId(e.target.value)}
              className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none"
            >
              <option value="" className="bg-[#120B19]">Nessun cliente (slot libero)</option>
              {clients
                .filter(c => (c.lessons_total - c.lessons_used) > 0)
                .map(c => (
                  <option key={c.id} value={c.id} className="bg-[#120B19]">
                    {c.name} ({c.lessons_total - c.lessons_used} lezioni)
                  </option>
                ))}
            </select>
            {bookForClientId && repeatCount > 0 && (
              <div className="text-[11px] text-white/50 mt-2">
                Il cliente verrà prenotato per lo slot iniziale e per tutte le ripetizioni future
                (una lezione decurtata per ogni prenotazione).
              </div>
            )}
          </div>

          <div className="label-caps mt-4">Servizio</div>
          <div className="grid gap-2 mt-1">
            {SERVICES.map((s) => (
              <button
                key={s.key}
                data-testid={`create-slot-${s.key}`}
                onClick={() => createSlot(s.key)}
                className={`p-4 border border-white/10 hover:border-[#A855F7] flex items-center justify-between text-left transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 flex items-center justify-center svc-${s.key}`}>{s.icon}</span>
                  <div>
                    <div className="font-heading font-bold uppercase tracking-wider text-sm">{s.label}</div>
                    <div className="text-xs text-white/50">Max {s.capacity} persone/ora{s.trainer_name ? ` · ${s.trainer_name}` : ""}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Slot details dialog */}
      <Dialog open={!!slotDialog} onOpenChange={(o) => !o && setSlotDialog(null)}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none w-[95vw] max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {slotDialog && SERVICES.find(s => s.key === slotDialog.service_type)?.label}
            </DialogTitle>
          </DialogHeader>
          {slotDialog && (
            <div className="space-y-4">
              <div className="text-sm text-white/60">
                {slotDialog.date} · {slotDialog.time} · {slotDialog.booked_count}/{slotDialog.capacity} posti
                {slotDialog.trainer_name && <span data-testid="slot-dialog-trainer"> · Allenatore: {slotDialog.trainer_name}</span>}
              </div>

              {role === "admin" && (
                <>
                  <div className="label-caps">Prenotazioni</div>
                  {slotDialog.bookings?.length ? slotDialog.bookings.map((b) => (
                    <div key={b.id} className="flex items-center justify-between border border-white/10 p-2 text-sm">
                      <span>{b.client_name}</span>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`reschedule-booking-${b.id}`}
                          onClick={() => openReschedule(b)}
                          className="text-[#A855F7] hover:underline text-xs"
                        >Sposta</button>
                        <button
                          data-testid={`cancel-booking-${b.id}`}
                          onClick={() => cancelBooking(b.id)}
                          className="text-[#FF3366] hover:underline text-xs"
                        >Annulla</button>
                      </div>
                    </div>
                  )) : <div className="text-white/40 text-sm">Nessuna prenotazione.</div>}

                  {slotDialog.booked_count < slotDialog.capacity && (
                    <div>
                      <div className="label-caps mb-2 mt-4">Prenota cliente</div>
                      <select
                        data-testid="select-client-book"
                        onChange={(e) => e.target.value && bookForClient(slotDialog.id, e.target.value)}
                        defaultValue=""
                        className="w-full bg-transparent border border-white/10 p-2 text-white"
                      >
                        <option value="" disabled className="bg-[#120B19]">Scegli cliente…</option>
                        {clients.filter(c => (c.lessons_total - c.lessons_used) > 0).map(c => (
                          <option key={c.id} value={c.id} className="bg-[#120B19]">
                            {c.name} ({c.lessons_total - c.lessons_used} rimaste)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <button data-testid="add-parallel-slot-btn" onClick={() => { const c = { date: slotDialog.date, time: slotDialog.time }; setSlotDialog(null); setCellDialog(c); }} className="w-full btn-ghost flex items-center justify-center gap-2">
                    + Aggiungi slot in parallelo (altro allenatore)
                  </button>
                  <button data-testid="delete-slot-btn" onClick={openDeleteDialog} className="w-full btn-ghost !text-[#FF3366] !border-[#FF3366]/30 flex items-center justify-center gap-2">
                    <Trash2 size={14} /> Elimina slot
                  </button>
                </>
              )}

              {role === "client" && (
                <>
                  {slotDialog.i_booked ? (
                    <div className="space-y-3">
                      <div className="text-[#10B981] text-sm">Sei prenotato per questa lezione.</div>
                      <button
                        data-testid="client-reschedule-btn"
                        onClick={async () => {
                          const mine = (await api.get("/bookings/mine")).data;
                          const b = mine.find(x => x.slot_id === slotDialog.id);
                          if (b) openReschedule(b);
                        }}
                        className="btn-ghost w-full"
                      >
                        Sposta prenotazione
                      </button>
                      <button
                        data-testid="client-cancel-btn"
                        onClick={async () => {
                          const mine = (await api.get("/bookings/mine")).data;
                          const b = mine.find(x => x.slot_id === slotDialog.id);
                          if (b) cancelBooking(b.id);
                        }}
                        className="btn-ghost w-full !text-[#FF3366] !border-[#FF3366]/30"
                      >
                        Annulla prenotazione (fino a 24h prima)
                      </button>
                    </div>
                  ) : slotDialog.booked_count >= slotDialog.capacity ? (
                    <div className="text-white/60 text-sm">Slot pieno.</div>
                  ) : (
                    <button data-testid="client-book-btn" onClick={() => clientBook(slotDialog.id)} className="btn-primary w-full">
                      Prenota
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete scope dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none w-[95vw] max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Elimina slot</DialogTitle>
          </DialogHeader>
          {deleteDialog && (
            <div className="space-y-3">
              <div className="text-sm text-white/70">
                {deleteDialog.date} · {deleteDialog.time} ·{" "}
                {SERVICES.find(s => s.key === deleteDialog.service_type)?.label}
              </div>

              <button
                data-testid="delete-scope-single"
                onClick={() => performDelete("single")}
                className="w-full p-4 border border-white/10 hover:border-[#FF3366] text-left transition-colors"
              >
                <div className="font-heading font-bold uppercase tracking-wider text-sm">Solo questo evento</div>
                <div className="text-xs text-white/50 mt-1">
                  Elimina lo slot e annulla le prenotazioni presenti su questo appuntamento.
                </div>
              </button>

              {deleteDialog.series_id && (deleteDialog.bookings || []).length > 0 && (
                (deleteDialog.bookings || []).map(b => (
                  <button
                    key={b.client_id}
                    data-testid={`delete-scope-series-client-${b.client_id}`}
                    onClick={() => performDelete("series_client", b.client_id)}
                    className="w-full p-4 border border-white/10 hover:border-[#A855F7] text-left transition-colors"
                  >
                    <div className="font-heading font-bold uppercase tracking-wider text-sm">
                      Tutti gli eventi futuri di {b.client_name}
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      Annulla le prenotazioni ricorrenti future di {b.client_name} in questa serie.
                      Gli slot restano disponibili per altri clienti.
                    </div>
                  </button>
                ))
              )}

              {deleteDialog.series_id && (
                <button
                  data-testid="delete-scope-series-all"
                  onClick={() => {
                    if (window.confirm("Sei sicura? Verranno eliminati tutti gli slot futuri della serie e annullate le prenotazioni.")) {
                      performDelete("series_all");
                    }
                  }}
                  className="w-full p-4 border border-[#FF3366]/40 hover:border-[#FF3366] text-left transition-colors"
                >
                  <div className="font-heading font-bold uppercase tracking-wider text-sm text-[#FF3366]">
                    Elimina tutta la serie ricorrente
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    Rimuove questo slot e ogni futuro slot ricorrente collegato,
                    con rimborso delle lezioni a tutti i clienti prenotati.
                  </div>
                </button>
              )}

              {!deleteDialog.series_id && (
                <div className="text-[11px] text-white/40 border border-white/5 p-3">
                  Questo slot non fa parte di una serie ricorrente.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduleDialog} onOpenChange={(o) => !o && setRescheduleDialog(null)}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none w-[95vw] max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Sposta prenotazione</DialogTitle>
          </DialogHeader>
          {rescheduleDialog && (
            <div className="space-y-4">
              <div className="text-sm text-white/60">
                Da: {rescheduleDialog.date} · {rescheduleDialog.time} ·{" "}
                {SERVICES.find(s => s.key === rescheduleDialog.service_type)?.label}
              </div>
              <div>
                <span className="label-caps block mb-2">Nuova data</span>
                <input
                  data-testid="reschedule-date"
                  type="date"
                  value={rescheduleForm.new_date}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, new_date: e.target.value })}
                  className="w-full bg-transparent border border-white/15 p-2 text-white focus:border-[#A855F7] outline-none"
                />
              </div>
              <div>
                <span className="label-caps block mb-2">Nuova ora</span>
                <input
                  data-testid="reschedule-time"
                  type="time"
                  step="3600"
                  value={rescheduleForm.new_time}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, new_time: e.target.value })}
                  className="w-full bg-transparent border border-white/15 p-2 text-white focus:border-[#A855F7] outline-none"
                />
              </div>
              <div className="text-[11px] text-white/40">
                Il tipo di servizio resta {SERVICES.find(s => s.key === rescheduleDialog.service_type)?.label}.
                Se non esiste uno slot alla nuova ora, verrà creato automaticamente.
              </div>
              <button data-testid="reschedule-confirm" onClick={doReschedule} className="btn-primary w-full">
                Conferma spostamento
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
